import {AccountId, TransactionId} from '@raison/types';
import {type} from 'arktype';
import BigNumber from 'bignumber.js';
import {identity} from 'effect/Function';
import * as Schema from 'effect/Schema';
import {CryptoCurrencyInfo, CryptoCurrencyType, Currency, CurrencyTrait, CurrencyTyped} from '~/entities/currencies';
import {BlockchainInfo} from '~/entities/operations-crypto';
import {Wish} from '@raison/prelude';

export const OrderId = type('number').as<TransactionId>();
export type OrderId = typeof OrderId.infer;

export const TrTxId = type('string').as<TrTxId>();
export const TrTxIdSchema = Schema.String.pipe(Schema.brand('TrTxId'));
export type TrTxId = typeof TrTxIdSchema.Type;

export type TrTxStatus = typeof TrTxStatus.Type;

const narrowTrTxStatus = (raw: typeof TrTxStatusWide.Type) => {
    if (
        raw === 'draft' ||
        raw === 'prepared' ||
        raw === 'provider_required_action' ||
        raw === 'provider_started_review'
    ) {
        return 'pending';
    } else {
        return raw;
    }
};

const TrTxStatusNarrow = Schema.Literal('pending', 'approved', 'rejected', 'completed');
const TrTxStatusWide = Schema.Literal(
    'draft',
    'prepared',
    'provider_required_action',
    'provider_started_review',
    ...TrTxStatusNarrow.literals
);
export const TrTxStatus = TrTxStatusWide.pipe(
    Schema.transform(TrTxStatusNarrow, {
        decode: narrowTrTxStatus,
        encode: identity,
    })
);

export const TrTxStatusWish = Wish.literal(...TrTxStatusWide.literals).pipe(Wish.morph(narrowTrTxStatus));

export interface TrTx {
    id: TrTxId;
    amount: BigNumber;
    status: TrTxStatus;
    currency: Currency;
    fromAccount: {
        id: AccountId;
        name: string;
        type: CryptoCurrencyType;
        blockchain: BlockchainInfo & CurrencyTrait;
    };
    toAddress: string;
    comment: string | null;
    additionalDetails: {
        name: string;
        value: string;
    }[];
}

export type SentTrTxPreview = {
    id: TrTxId;
    amount: BigNumber;
    status: TrTxStatus;
    currency: CurrencyTyped;
};

export type ReceiveTxPreview = {
    id: OrderId;
    amount: BigNumber;
    currency: CryptoCurrencyInfo;
};
